import React, {
	type ReactNode,
	createContext,
	useContext,
	useRef,
	useCallback,
	useMemo,
} from 'react';
import type { HoverPopoverRefContent } from '../types.tsx';
import { useLazyRef } from '../utils.tsx';

type DeleteListener = (fieldId: string) => void;
interface HoverPopoverContentContextValue {
	setCellIdByRef: (cell: HTMLTableCellElement, fieldId: string) => void;
	deleteCellIdByRef: (cell: HTMLTableCellElement) => void;
	getCellIdByRef: (cell: HTMLTableCellElement) => string | undefined;
	setHoverPopoverContentById: (fieldId: string, content: HoverPopoverRefContent) => void;
	deleteHoverPopoverContentById: (fieldId: string) => void;
	getHoverPopoverContentById: (fieldId: string) => HoverPopoverRefContent | undefined;
	subscribeHoverPopoverContentDeleteById: (fieldId: string, listener: DeleteListener) => () => void;
}
interface HoverPopoverContentProviderProps {
	children: ReactNode;
}

const HoverPopoverContentContext = createContext<HoverPopoverContentContextValue | undefined>(
	undefined,
);

export const HoverPopoverContentProvider: React.FC<HoverPopoverContentProviderProps> = ({
	children,
}) => {
	/**
	 * cellRefFieldIdMap is a map of cell refs to field IDs. This is used by the
	 * event delegator to lookup which field is hovered by table cell ref.
	 */
	const cellRefFieldIdMap = useLazyRef(() => new Map<HTMLTableCellElement, string>());
	/**
	 * fieldIdPopoverContentMap is a map of ARI field IDs to hover popover content.
	 * This is used by the hover popover state provider to lookup what the content for a hover popover
	 * is by a given field ID. In conjunction with the cellRefFieldIdMap above, this allows us to
	 * lookup the content for a hover popover by a given table cell ref.
	 */
	const fieldIdPopoverContentMap = useLazyRef(() => new Map<string, HoverPopoverRefContent>());
	/**
	 * deleteListeners is a map of ARI field IDs to a set of delete listeners. See comments below on deleteHoverPopoverContentById.
	 */
	const deleteListeners = useLazyRef(() => new Map<string, Set<DeleteListener>>());

	const setCellIdByRef = useCallback(
		(cell: HTMLTableCellElement, fieldId: string) => {
			cellRefFieldIdMap.current.set(cell, fieldId);
		},
		[cellRefFieldIdMap],
	);

	const getCellIdByRef = useCallback(
		(cell: HTMLTableCellElement) => {
			return cellRefFieldIdMap.current.get(cell);
		},
		[cellRefFieldIdMap],
	);

	const deleteCellIdByRef = useCallback(
		(cell: HTMLTableCellElement) => {
			cellRefFieldIdMap.current.delete(cell);
		},
		[cellRefFieldIdMap],
	);

	const setHoverPopoverContentById = useCallback(
		(fieldId: string, content: HoverPopoverRefContent) => {
			fieldIdPopoverContentMap.current.set(fieldId, content);
		},
		[fieldIdPopoverContentMap],
	);

	const getHoverPopoverContentById = useCallback(
		(fieldId: string) => {
			return fieldIdPopoverContentMap.current.get(fieldId);
		},
		[fieldIdPopoverContentMap],
	);

	/**
	 * When a user hovers on a cell a popover opens. Then if they open inline edit we need the popover to close.
	 *
	 * The popover is attached using the hoverPopoverContentRef. When the popup is mounted, subscribeHoverPopoverContentDeleteById
	 * is called which allows us to subscribe to calls to deleteHoverPopoverContentById. When the component unmounts, the hover popover content ref will call this
	 * delete method (i.e user switches from read to edit mode), which calls any listeners (e.g closePopup).
	 */
	const deleteHoverPopoverContentById = useCallback(
		(fieldId: string) => {
			const isDeleted = fieldIdPopoverContentMap.current.delete(fieldId);
			if (isDeleted && deleteListeners.current.has(fieldId)) {
				for (const listener of deleteListeners.current.get(fieldId) ?? []) {
					listener(fieldId);
				}
				deleteListeners.current.delete(fieldId);
			}
		},
		[fieldIdPopoverContentMap, deleteListeners],
	);

	const subscribeHoverPopoverContentDeleteById = useCallback(
		(fieldId: string, listener: DeleteListener) => {
			if (!deleteListeners.current.has(fieldId)) {
				deleteListeners.current.set(fieldId, new Set());
			}

			deleteListeners.current.get(fieldId)?.add(listener);

			return () => {
				const listeners = deleteListeners.current.get(fieldId);
				if (listeners) {
					listeners.delete(listener);
					if (listeners.size === 0) {
						deleteListeners.current.delete(fieldId);
					}
				}
			};
		},
		[deleteListeners],
	);

	const contextValue: HoverPopoverContentContextValue = useMemo(
		() => ({
			setCellIdByRef,
			deleteCellIdByRef,
			getCellIdByRef,
			setHoverPopoverContentById,
			deleteHoverPopoverContentById,
			getHoverPopoverContentById,
			subscribeHoverPopoverContentDeleteById,
		}),
		[
			setCellIdByRef,
			deleteCellIdByRef,
			getCellIdByRef,
			setHoverPopoverContentById,
			deleteHoverPopoverContentById,
			getHoverPopoverContentById,
			subscribeHoverPopoverContentDeleteById,
		],
	);

	return (
		<HoverPopoverContentContext.Provider value={contextValue}>
			{children}
		</HoverPopoverContentContext.Provider>
	);
};

export const useHoverPopoverContent = (): HoverPopoverContentContextValue => {
	const context = useContext(HoverPopoverContentContext);
	if (context === undefined) {
		throw new Error('useHoverPopoverContent must be used within a HoverPopoverContentProvider');
	}
	return context;
};

export type HoverPopoverContentRef = ((node: HTMLElement | null) => void) | null;

// Returns a ref to bind to hover popover content.
// It adds the field's ARI fieldId and its corresponding content as well as the node its appended to to the fieldIdPopoverContentMap.
// lastNode ensures that when the content ref unmounts, the fieldId and its corresponding content is removed from the fieldIdPopoverContentMap.
export const useHoverPopoverContentRef = (fieldId?: string) => {
	const lastNode = useRef<HTMLElement | null>(null);
	const { setHoverPopoverContentById, deleteHoverPopoverContentById } = useHoverPopoverContent();

	const hoverPopoverContentRefFactory = useCallback(
		(content: Omit<HoverPopoverRefContent, 'contentElement'>) => (node: HTMLElement | null) => {
			if (node && fieldId) {
				setHoverPopoverContentById(fieldId, { ...content, contentElement: node });
				lastNode.current = node;
			} else if (lastNode.current && fieldId) {
				deleteHoverPopoverContentById(fieldId);
				lastNode.current = null;
			}
		},
		[fieldId, setHoverPopoverContentById, deleteHoverPopoverContentById],
	);
	return hoverPopoverContentRefFactory;
};
